import { call, put, select, takeLatest } from 'redux-saga/effects';

import {
  getModels as getModelsApi,
  createModel as createModelApi,
  updateModel as updateModelApi,
  deleteModel as deleteModelApi,
  updateSceneConfig as updateSceneConfigApi,
} from '../../services';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { doUpdateComponent, getComponentHashmap } from '../components';
import { addFile, initUpload, getAdded } from '../uploads';
import {
  REQUEST_MODELS,
  CREATE_MODEL_AND_INITIAL_VERSION,
  ADD_MODEL_VERSION,
  UPDATE_MODEL,
  DELETE_MODEL,
  UPDATE_SCENE_CONFIG,
  receiveModels,
  addUploadingId,
} from './actions';
import { requestModelThumbnail } from '../sites/actions';

export function* doRequestModels(action) {
  const { siteId } = action;
  try {
    const { values: models } = yield call(getModelsApi, siteId);
    yield put(receiveModels(models, siteId));
  } catch (e) {
    console.error('Unable to fetch models: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getModels', 'error')()));
    yield put(receiveModels([], siteId));
  }
}

export function* doCreateModelAndUploadFile(action) {
  const {
    data: { site, org, name, modelType, file, rootFile },
    componentId,
  } = action;

  const payload = {
    site,
    org,
    name,
    modelType,
  };

  try {
    const { id: modelId } = yield call(createModelApi, payload);
    yield put(addUploadingId(modelId));
    const comps = yield select(getComponentHashmap);
    const comp = comps[componentId];
    if (comp) {
      yield call(doUpdateComponent, {
        id: componentId,
        siteId: site,
        data: {
          models: [...comp.models, { modelId, customName: name }],
        },
        doNotify: false,
      });
    }
    yield call(doRequestModels, { siteId: site });
    yield put(addFile(file, site.id, modelId));
    const addedFiles = yield select(getAdded);

    yield put(
      initUpload({
        id: addedFiles[0].id,
        comment: 'Initial upload',
        org,
        rootFile,
        siteId: site.id,
      })
    );

    yield put(displayNotification(getNotification('createModel', 'success')(name)));
  } catch (e) {
    console.error('Unable to create model: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createModel', 'error')()));
  }
}

export function* doAddModelVersion(action) {
  const {
    data: { site, org, file, rootFile, comment, copyMappings, mappingsFromVersion },
    modelId,
  } = action;

  try {
    yield put(addFile(file, site.id, modelId));
    const [addedFile] = yield select(getAdded);

    if (addedFile) {
      yield put(
        initUpload({
          id: addedFile.id,
          org,
          rootFile,
          siteId: site.id,
          comment,
          copyMappings,
          mappingsFromVersion,
        })
      );
      yield call(doRequestModels, { siteId: site });
      yield put(displayNotification(getNotification('addModelVersion', 'success')()));
    }
  } catch (e) {
    console.error('Unable to create model: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('addModelVersion', 'error')()));
  }
}

export function* doUpdateModel(action) {
  const { modelId, data } = action;

  try {
    yield updateModelApi(modelId, data);
    yield call(doRequestModels, { siteId: data.site });
    yield put(displayNotification(getNotification('updateModel', 'success')(data.name, modelId)));
  } catch (e) {
    console.error('Unable to update model: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateModel', 'error')(modelId)));
  }
}

export function* doDeleteModel(action) {
  const { siteId, modelId } = action;
  const model = yield select((state) => state.models.hashmap[modelId]);
  try {
    yield deleteModelApi(modelId);
    yield call(doRequestModels, { siteId });
    yield put(displayNotification(getNotification('deleteModel', 'success')(model.name, modelId)));
  } catch (e) {
    console.error('Unable to delete model: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteModel', 'error')(modelId)));
  }
}

export function* doUpdateSceneConfig(action) {
  const { siteId, modelId, versionId, data } = action;

  try {
    yield updateSceneConfigApi(modelId, versionId, data);
    yield call(doRequestModels, { siteId });
    yield put(requestModelThumbnail(modelId, versionId));
    yield put(displayNotification(getNotification('updateSceneConfig', 'success')(modelId)));
  } catch (e) {
    console.error('Unable to update scene configuration: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateSceneConfig', 'error')(modelId)));
  }
}

export const sagas = [
  takeLatest(REQUEST_MODELS, doRequestModels),
  takeLatest(CREATE_MODEL_AND_INITIAL_VERSION, doCreateModelAndUploadFile),
  takeLatest(ADD_MODEL_VERSION, doAddModelVersion),
  takeLatest(UPDATE_MODEL, doUpdateModel),
  takeLatest(DELETE_MODEL, doDeleteModel),
  takeLatest(UPDATE_SCENE_CONFIG, doUpdateSceneConfig),
];
